import { useState } from 'react';
import { Alert, Button, Link, TextInput } from '@gravity-ui/uikit';
import { useSession, useSessionStore } from '../../lib/session/context.js';
import { EntryPage } from '../../ui/entry-page.js';
import { Field } from '../../ui/field.js';
import { ForgotPasswordForm } from './forgot-password-form.js';

/**
 * Вход в платформенную админку.
 *
 * Два шага без исключений: пароль, затем код из приложения-аутентификатора.
 * У аккаунта с правом менять деньги любой клиники платформы MFA обязательна
 * с первого дня (docs/03-modules/20-platform-admin.md §2).
 */

function ErrorNote({ error }: { error: string | null }) {
  if (error === null) return null;
  return <Alert theme="danger" message={error} />;
}

function MfaStep({ email, error, busy }: { email: string; error: string | null; busy: boolean }) {
  const store = useSessionStore();
  const [code, setCode] = useState('');

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void store.verifyMfa(code.trim());
      }}
      className="flex flex-col gap-4"
    >
      <p className="text-[var(--g-color-text-secondary)]">
        Для входа как <b className="text-[var(--g-color-text-primary)]">{email}</b> нужен код из приложения-аутентификатора.
      </p>
      <Field label="Код подтверждения" required>
        {(id) => (
          <TextInput
            id={id}
            size="l"
            value={code}
            onUpdate={setCode}
            autoFocus
            name="code"
            autoComplete="one-time-code"
            controlProps={{ inputMode: 'numeric' }}
          />
        )}
      </Field>
      <ErrorNote error={error} />
      <div className="flex flex-col gap-2">
        <Button type="submit" view="action" size="l" width="max" loading={busy}>
          Подтвердить
        </Button>
        <Button
          type="button"
          view="flat-secondary"
          size="l"
          width="max"
          disabled={busy}
          onClick={() => {
            store.cancelMfa();
          }}
        >
          Назад
        </Button>
      </div>
    </form>
  );
}

export function LoginScreen() {
  const store = useSessionStore();
  const session = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [forgot, setForgot] = useState(false);

  const busy = (session.status === 'anonymous' || session.status === 'mfa_required') && session.busy;
  const error = session.status === 'anonymous' || session.status === 'mfa_required' ? session.error : null;

  if (session.status === 'mfa_required') {
    return (
      <EntryPage title="Подтверждение входа" product="Админка платформы">
        <MfaStep email={session.email} error={error} busy={busy} />
      </EntryPage>
    );
  }

  if (forgot) {
    return (
      <EntryPage title="Восстановление пароля" product="Админка платформы">
        <ForgotPasswordForm
          initialEmail={email.trim()}
          onBack={() => {
            setForgot(false);
          }}
        />
      </EntryPage>
    );
  }

  return (
    <EntryPage title="Вход" hint="Подписки и оплаты клиник, менеджеры, деньги платформы" product="Админка платформы">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void store.login(email.trim(), password);
        }}
        className="flex flex-col gap-4"
      >
        <Field label="Почта" required>
          {(id) => (
            <TextInput
              id={id}
              size="l"
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              value={email}
              onUpdate={setEmail}
            />
          )}
        </Field>
        <Field label="Пароль" required>
          {(id) => (
            <TextInput
              id={id}
              size="l"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onUpdate={setPassword}
            />
          )}
        </Field>
        <Link
          href="#"
          className="-mt-1 w-fit"
          onClick={(event) => {
            event.preventDefault();
            setForgot(true);
          }}
        >
          Забыли пароль?
        </Link>
        <ErrorNote error={error} />
        <Button type="submit" view="action" size="l" width="max" loading={busy}>
          Войти
        </Button>
      </form>
    </EntryPage>
  );
}
